"use client";

import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, FileText, GraduationCap, Mail, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function AcademicEngagement() {
  const shouldReduceMotion = useReducedMotion();
  const engagementOptions = [{
    id: "consultation",
    icon: Calendar,
    title: "Schedule Consultation",
    description: "Discuss experimental methodologies, instrumentation requirements, and grant specifications with our analytical specialists.",
    badge: "Direct Support",
    actionText: "Book Session",
    actionHref: "/ContactPage"
  }, {
    id: "access",
    icon: FileText,
    title: "Request Instrument Access",
    description: "Explore demonstration units, technology evaluations, and qualification frameworks for university analytical laboratories.",
    badge: "Facility Access",
    actionText: "Request Access",
    actionHref: "/ContactPage"
  }, {
    id: "contact",
    icon: Mail,
    title: "Academic Partnership",
    description: "Inquire about long-term institutional agreements, shared core facility packages, and educational instrument training.",
    badge: "Institutional",
    actionText: "Contact Team",
    actionHref: "/ContactPage"
  }];
  const benefits = ["Dedicated technical support for university laboratories", "Assistance with research grant instrumentation specifications", "Full lifecycle qualification and calibration protocols", "Application testing for specialized research projects"];
  return <section id="academicengagementcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Main CTA Container */}
        <div className="relative rounded-2xl bg-secondary/60 border border-border overflow-hidden p-6 md:p-12 lg:p-16">
          {/* Subtle Decorative Elements */}
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 rounded-full bg-accent/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto text-center space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Badge variant="outline" className="px-3 py-1 gap-1.5 bg-background">
                <GraduationCap className="w-3.5 h-3.5 text-primary" />
                <span>Academic & Research Collaboration</span>
              </Badge>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
              Partner With Us for Rigorous Academic Research
            </h2>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              We collaborate with university departments, research institutes, and core facilities across Ireland and the UK to provide compliant analytical instrumentation, qualification protocols, and ongoing technical guidance.
            </p>
          </div>

          {/* Cards Grid */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
            {engagementOptions.map((option, i) => {
            const IconComponent = option.icon;
            return <motion.div key={option.id} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="h-full">
                  <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-colors flex flex-col justify-between shadow-sm">
                    <CardContent className="p-6 flex flex-col h-full justify-between gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-primary">
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {option.badge}
                          </Badge>
                        </div>

                        <h3 className="text-lg font-semibold text-foreground">
                          {option.title}
                        </h3>

                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {option.description}
                        </p>
                      </div>

                      <div className="pt-2">
                        <Button asChild className="w-full" variant={i === 0 ? "default" : "outline"}>
                          <Link to={option.actionHref} className="gap-2">
                            <span>{option.actionText}</span>
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>

          {/* Value Checklist Footer */}
          <div className="mt-12 pt-8 border-t border-border/70 relative z-10">
            <div className="text-center mb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                Research Support Highlights
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground/90">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}